import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PenLine } from "lucide-react";
import Lottie from "react-lottie-player";
import LoadingAnimationHands from "@/assets/animations/loadingAnimationHands.json";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import TextDiffAnalyzer from "./TextDiffAnalyzer";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useEffect, useMemo, useState } from "react";
import { EditFeedback } from "./EditFeedback";
import useToastStore from "@/shared/zustand/toastStore";
import { Skeleton } from "primereact/skeleton";
import { updateFeedbackUpsert } from "@/core/api/api";
const TextPairAnalyzer = ({ oldText, newText, modeAssistantName, modeName, index, onProvideFeedback, }) => {
    const [searchStatus, setSearchStatus] = useState("idle");
    const [feedbackList, setFeedbackList] = useState([]);
    const showToast = useToastStore((state) => state.showToast);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const { search, isLoading, results } = useDebouncedSearch({
        shallCallGraphqlCall: false,
        delay: 1000,
    });
    useEffect(() => {
        if (oldText && modeAssistantName) {
            setSearchStatus("searching");
            search(oldText, modeAssistantName);
        }
    }, [oldText, modeAssistantName]);
    useEffect(() => {
        if (!isLoading && searchStatus === "searching") {
            setSearchStatus("complete");
        }
    }, [isLoading]);
    useMemo(() => {
        if (results.length > 0) {
            const mappedFeedback = results.map((result) => ({
                id: result.id,
                originalContent: result.originalContent,
                suggestedReplacement: result.suggestedReplacement,
                applyTo: result.applyTo,
                whenToApply: result.whenToApply,
                whenNotToApply: result.whenNotToApply,
            }));
            setFeedbackList(mappedFeedback);
        }
        else if (searchStatus === "complete") {
            setFeedbackList([]);
        }
    }, [results, searchStatus]);
    const handleEdit = (feedback) => {
        setSelectedFeedback(feedback);
        setIsEditing(true);
    };
    const handleUpdateFeedback = async (updatedData) => {
        try {
            const updatedList = feedbackList.map((item) => item.id === updatedData.id ? updatedData : item);
            if (!modeAssistantName) {
                console.error("No mode assistant name provided");
                showToast("error", "Error", "Provide the mode assistant name");
                return;
            }
            const tableName = `${modeAssistantName}-agentGuide`;
            setFeedbackList(updatedList);
            // const upsertFeedbackPayload = {
            //   query: updatedData.originalContent || "", // inputQuestion
            //   metadata: {
            //     text: updatedData.originalContent || "",
            //   },
            //   variantsToAdd: [
            //     {
            //       variant: updatedData.suggestedReplacement || "",
            //       conditions_whentoapply: updatedData.whenToApply || "",
            //       conditions_whennottoapply: updatedData.whenNotToApply || "",
            //     },
            //   ],
            //   feedbackToAdd: updatedData.suggestedReplacement || "", // feedback
            //   // whenToApply
            //   //whenNotToApply
            //   //assistantName
            //   //scope - > assistant | global
            //   numberOfVariants: 1,
            //   tableName: tableName,
            // };
            const upsertFeedbackPayload = {
                inputQuestion: updatedData.originalContent || "", // inputQuestion
                feedback: updatedData.suggestedReplacement || "", // feedback
                whenToApply: updatedData.whenToApply || "", // whenToApply
                whenNotToApply: updatedData.whenNotToApply || "", //whenNotToApply
                assistantName: modeAssistantName, // assistantName
                scope: "Assistant", //scope - > Assistant | Global
            };
            // old code of api call
            // const responseFromUpsert = await axios.post(
            //   `${process.env.VITE_BASE_API_URL}/feedback/upsert_feedback`,
            //   upsertFeedbackPayload
            // );
            const { responseData, error } = await updateFeedbackUpsert(upsertFeedbackPayload);
            if (error) {
                console.error("Error updating feedback:", error);
                showToast("error", "Error", "Error updating feedback");
                return;
            }
            // console.log("Feedback upserted:", responseData?.data);
            showToast("success", "Success", "Feedback saved successfully");
            setIsEditing(false);
            setSelectedFeedback(null);
        }
        catch (error) {
            console.error("Error updating feedback:", error);
            showToast("error", "Error", "Error updating feedback");
        }
    };
    const handleProvideFeedback = () => {
        if (modeAssistantName && modeName) {
            const feedbackDataPaylaod = {
                feedbackData: {
                    originalContent: oldText,
                    suggestedReplacement: newText,
                },
                modeName: modeName,
                modeAssistantName: modeAssistantName,
            };
            onProvideFeedback(feedbackDataPaylaod);
        }
        else {
            console.error("No mode name or assistant name provided");
            showToast("error", "Error", "No mode name or assistant name provided");
        }
    };
    const renderContent = () => {
        if (searchStatus === "idle")
            return null;
        if (searchStatus === "searching" || isLoading) {
            return (_jsxs("div", { className: "w-full h-[50px] bg-[#fff] border border-[#d1d5db] rounded-[6px] p-4 flex flex-row justify-between items-center", children: [_jsx(Skeleton, { width: "30%", height: "14px" }), _jsx(Skeleton, { width: "14px", height: "14px", className: "mr-2" })] }));
        }
        if (feedbackList?.length > 0) {
            return (_jsx(Accordion, { className: "border border-[#d1d5db] shadow-none rounded-[6px]", children: _jsx(AccordionTab, { header: `${feedbackList.length} Variant${feedbackList.length > 1 ? "s" : ""}`, className: "shadow-none rounded-[6px] m-0", headerClassName: "text-sm shadow-none rounded-[6px]", contentClassName: "rounded-b-[6px]", children: _jsx("div", { className: "space-y-2", children: feedbackList.map((feedback) => (_jsxs("div", { className: "flex justify-between items-center p-2 bg-gray-50 rounded text-sm", children: [_jsx("span", { children: feedback.suggestedReplacement }), _jsx("i", { className: "pi pi-pencil cursor-pointer", onClick: () => handleEdit(feedback) })] }, feedback.id))) }) }) }));
        }
        return (_jsx("div", { className: "", children: _jsxs(Button, { className: "rounded-[6px] px-4 py-2 text-sm text-white flex items-center gap-x-2 transition-all duration-300 bg-gradient-to-r from-[#2546F0] to-[#5928ED] hover:from-[#0039cc] hover:to-[#0039cc]", onClick: handleProvideFeedback, children: [_jsx(PenLine, { size: 16 }), _jsx("span", { children: "Provide Instant Learning" })] }) }));
    };
    // return (
    // <div className="mb-4 border-b last:border-b-0">
    //   <TextDiffAnalyzer oldTexts={[oldText]} newTexts={[newText]} />
    //   {renderContent()}
    // </div>
    // );
    return isEditing && selectedFeedback ? (_jsx("div", { className: "border border-[#5928ed] p-4 bg-gradient-to-br from-[#f4f6fe] to-[#f6f4fe] rounded-[8px] shadow-none", children: _jsx(EditFeedback, { data: selectedFeedback, onSave: handleUpdateFeedback, modeName: modeName, onCancel: () => {
                setIsEditing(false);
                setSelectedFeedback(null);
            } }) })) : (_jsxs("div", { className: "p-4 bg-gradient-to-br from-[#f4f6fe] to-[#f6f4fe] rounded-[8px] shadow-none", children: [_jsx(TextDiffAnalyzer, { oldTexts: [oldText], newTexts: [newText] }), _jsx("div", { className: "flex flex-col gap-y-3", children: renderContent() })] }));
};
// Main component
export const InstantLearningContent = ({ onProvideFeedback, instantLearningData, }) => {
    if (!instantLearningData?.instantLearningData) {
        // return null;
        return (_jsx("div", { className: "w-full h-full m-auto flex flex-col justify-center items-center p-6", children: _jsx(Lottie, { animationData: LoadingAnimationHands, loop: true, play: true, className: "w-full md:w-1/2 mx-auto" }) }));
    }
    return (_jsx("div", { className: "h-full flex flex-col gap-y-4 font-content overflow-y-auto", children: instantLearningData.instantLearningData.oldTexts.map((oldText, index) => (_jsx(TextPairAnalyzer, { oldText: oldText, newText: instantLearningData.instantLearningData.newTexts[index], modeAssistantName: instantLearningData.modeAssistantName, modeName: instantLearningData.modeName, index: index, onProvideFeedback: onProvideFeedback }, index))) }));
};
export default InstantLearningContent;
